import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import axios from "axios"
import { StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

// Configure axios to include CSRF token from meta tag
axios.defaults.xsrfCookieName = ""
axios.defaults.xsrfHeaderName = "X-CSRF-Token"
axios.interceptors.request.use((config) => {
  const token = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  if (token) {
    config.headers["X-CSRF-Token"] = token
  }
  return config
})

const appName = import.meta.env.VITE_APP_NAME ?? "StitchPDF"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => {
    if (!title) return appName
    if (title.includes("StitchPDF")) return title
    return `${title} - ${appName}`
  },

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/**/*.tsx",
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    page.default.layout ??= [PersistentLayout]

    return page
  },

  setup({ el, App, props }) {
    if (el.hasChildNodes()) {
      hydrateRoot(
        el,
        <StrictMode>
          <App {...props} />
        </StrictMode>,
      )
      return
    }
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
